import React, { useEffect, useRef, useState } from 'react';
import { animateScroll as scroll } from 'react-scroll';
import { useParams, useHistory } from 'react-router-dom';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { getAuthClient } from '../../utils/auth';
import { buildYourTourApiUrl } from '../../utils/ApiHelpers';
import { useAuthStore } from '../../stores';

import useBrochureVisitBeacon from '../../hooks/useBrochureVisitBeacon';
import BrochurePageHeader from './BrochurePageHeader';
import BrochurePageContent from './BrochurePageContent';
import BrochurePageSkeleton from './BrochurePageSkeleton';
import FadingContainer from '../FadingContainer/FadingContainer';

import './BrochurePage.scss';
import './BrochurePageWrapper.scss';

const auth = getAuthClient();

// get current year
const date = new Date();
const year = date.getFullYear();

// What a prospect sees when the link does not resolve. Before this existed the
// shell set brochureData to {} on any failure, which is truthy — so the
// skeleton faded out and the page rendered its header, an empty content pane
// and the Vectre footer: a blank branded page that says nothing went wrong and
// nothing to do about it (issue #310).
//
// Two cases, because they call for two different actions. An expired link is
// answered by the community reissuing it; anything else is a link that never
// addressed a brochure, and re-sending it would not help.
const EXPIRED_MESSAGE = {
  heading: 'This brochure link has expired',
  body: 'Links to a personalized brochure stay active for a limited time. Please contact the community and ask them to send you a fresh link — everything you saved is still there.',
};

const UNAVAILABLE_MESSAGE = {
  heading: 'We could not open this brochure',
  body: 'This link does not point to a brochure we can find. Please check that you followed the whole link, or contact the community for a new one.',
};

/**
 * Which message a failed fetch calls for.
 *
 * Drupal names the expired case explicitly with a machine code rather than
 * leaving the client to pattern-match on prose, so a copy edit on the server
 * can never silently turn an expired link back into a generic error.
 */
export const brochureErrorMessage = (data) => (
  data?.code === 'brochure_link_expired' ? EXPIRED_MESSAGE : UNAVAILABLE_MESSAGE
);

const DEFAULT_FOOTER_MESSAGE = (
  <>
    Your Interactive Brochure is brought to you by
    {' '}
    <span>YOUR</span>
    TOUR, a sales enablement tool by THE VECTRE.
  </>
);

/**
 * Config-driven shell for brochure-style pages (PIB and future variants).
 *
 * Owns the shared behavior: authenticated fetch, skeleton/content fade
 * orchestration, page title, header + panes layout, back-to-top, and footer.
 * Page-specific concerns are injected via props:
 *   - apiPath          which `/your_tour_api/<apiPath>/<nid>` endpoint to fetch
 *                      (used by the default single-call fetch)
 *   - getApiParams     (params) => extra query params for that apiPath fetch
 *   - fetchData        (params) => Promise<data> — optional custom fetcher for
 *                      pages that assemble data from more than one endpoint
 *                      (e.g. the TourGuide AI brochure). Overrides apiPath.
 *   - getDocumentTitle (data) => document.title string (must handle null data)
 *   - getHeaderData    (data, params) => props for BrochurePageHeader
 *   - getContentProps  (data, params) => props spread into BrochurePageContent
 *   - onDataLoaded     (data) => void side effect after a successful fetch
 */
const BrochurePage = ({
  apiPath,
  getApiParams,
  fetchData,
  getDocumentTitle,
  getHeaderData,
  getContentProps,
  onDataLoaded,
  footerMessage,
  footerLinks,
}) => {
  const [brochureData, setBrochureData] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isContentRendered, setIsContentRendered] = useState(false);
  const [isContentFaded, setIsContentFaded] = useState(false);

  const params = useParams();
  const { drupalNid, communitySlug, sanitizedTitle } = params;
  const history = useHistory();

  const isAuthed = useAuthStore((state) => state.isAuthed);

  // Creds from the response gate first-party engagement beacons. Every brochure
  // that supports tracking returns a source-agnostic `tracking` block
  // ({ endpoint, token, identity }); brochures that don't stay inert.
  const tracking = brochureData?.tracking;
  useBrochureVisitBeacon({ tracking });

  // Hold the latest onDataLoaded so the fetch effect need not depend on it
  // (avoids re-fetching when the parent passes a fresh inline callback).
  const onDataLoadedRef = useRef(onDataLoaded);
  useEffect(() => {
    onDataLoadedRef.current = onDataLoaded;
  }, [onDataLoaded]);

  const handleBackToTopClick = () => {
    scroll.scrollToTop({
      duration: 500,
      smooth: 'easeInOutCubic',
    });
  };

  // Fetch all brochure data in a single optimized API call
  useEffect(() => {
    const fetchBrochureData = async () => {
      try {
        const data = fetchData
          ? await fetchData({ drupalNid, communitySlug })
          : await auth.fetchWithAuthentication(
            buildYourTourApiUrl(
              `/your_tour_api/${apiPath}/${drupalNid}`,
              getApiParams ? getApiParams({ drupalNid, communitySlug, sanitizedTitle }) : {},
            ),
            'GET',
            null,
          );

        if (data && !data.error) {
          // The subject of this brochure was merged into another (residents can
          // be merged in the CMS) — move to the survivor's canonical brochure
          // URL. Use the path rather than the absolute URL so it works on any
          // host.
          if (data.redirect?.url) {
            let redirectPath = null;
            try {
              redirectPath = new URL(data.redirect.url).pathname;
            } catch (e) {
              redirectPath = null;
            }
            if (redirectPath && redirectPath !== window.location.pathname) {
              // Drop the payload we are navigating away from before replacing
              // the URL. history.replace fires no pagehide, so without this the
              // lead's tracking creds stay live: its visit is never closed, and
              // any further engagement on the stale content is recorded against
              // the lead this brochure has stopped being. Clearing it ends the
              // visit and shows the skeleton until the survivor's data lands.
              setBrochureData(null);
              setIsContentRendered(false);
              setIsContentFaded(false);
              history.replace(redirectPath);
              return;
            }
          }

          setBrochureData(data);
          if (onDataLoadedRef.current) {
            onDataLoadedRef.current(data);
          }
        } else {
          console.error('Error fetching brochure data:', data?.error);
          setErrorMessage(brochureErrorMessage(data));
          setBrochureData({});
        }
      } catch (error) {
        console.error('Error fetching brochure data:', error);
        // A thrown request carries no server code, so it can only be the
        // generic message: we do not know that the link expired.
        setErrorMessage(UNAVAILABLE_MESSAGE);
        setBrochureData({});
      }
    };

    if (isAuthed && drupalNid) {
      fetchBrochureData();
    }
  }, [
    isAuthed,
    drupalNid,
    communitySlug,
    sanitizedTitle,
    apiPath,
    getApiParams,
    fetchData,
    history,
  ]);

  // Set page title.
  const documentTitle = getDocumentTitle(brochureData);
  useEffect(() => {
    if (documentTitle) {
      document.title = documentTitle;
    }
  }, [documentTitle]);

  // Wait for data before starting fade-in
  useEffect(() => {
    if (brochureData && !isContentRendered) {
      const timer = setTimeout(() => {
        setIsContentRendered(true);
      }, 50);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [brochureData, isContentRendered]);

  // Track when fade is complete to swap positions
  useEffect(() => {
    if (isContentRendered && !isContentFaded) {
      const timer = setTimeout(() => {
        setIsContentFaded(true);
      }, 500); // Match the CSS transition duration
      return () => {
        clearTimeout(timer);
      };
    }
    return undefined;
  }, [isContentRendered, isContentFaded]);

  const headerData = brochureData ? getHeaderData(brochureData, params) : null;
  const contentProps = brochureData ? getContentProps(brochureData, params) : {};

  return (
    <FadingContainer>
      <div className="pibPage-wrapper">
        <BrochurePageSkeleton
          className={classNames('pibPage pibPage-skeleton', {
            'pibPage-skeleton--hidden': isContentFaded,
          })}
        />

        <div
          className={classNames('pibPage pibPage-content', {
            'pibPage-content--fadeIn': isContentRendered,
            'pibPage-content--visible': isContentFaded,
          })}
        >
          {errorMessage ? (
            <div className="pibPage-error" role="alert">
              <h1 className="pibPage-error__heading">{errorMessage.heading}</h1>
              <p className="pibPage-error__body">{errorMessage.body}</p>
            </div>
          ) : (
            <>
              <BrochurePageHeader headerData={headerData} />

              <BrochurePageContent tracking={tracking} {...contentProps} />
            </>
          )}

          {!errorMessage && (
          <div className="pibPage-backToTop">
            <button
              type="button"
              className="pibPage-backToTop__button"
              onClick={handleBackToTopClick}
            >
              <span className="pibPage-backToTop__button__arrow" />
              <span className="pibPage-backToTop__button__text">Back to Top</span>
            </button>
          </div>
          )}

          <div className="pibPage-vectre">
            <div className="pibPage-vectre__message">{footerMessage}</div>
            <div className="pibPage-vectre__colophon">
              Copyright ©
              {' '}
              {year}
              {' '}
              The Vectre - All Rights Reserved
            </div>
            {footerLinks && (
              <div className="pibPage-vectre__links">{footerLinks}</div>
            )}
          </div>
        </div>
      </div>
    </FadingContainer>
  );
};

BrochurePage.propTypes = {
  apiPath: PropTypes.string,
  getApiParams: PropTypes.func,
  fetchData: PropTypes.func,
  getDocumentTitle: PropTypes.func,
  getHeaderData: PropTypes.func,
  getContentProps: PropTypes.func,
  onDataLoaded: PropTypes.func,
  footerMessage: PropTypes.node,
  footerLinks: PropTypes.node,
};

BrochurePage.defaultProps = {
  apiPath: null,
  getApiParams: null,
  fetchData: null,
  getDocumentTitle: () => '',
  getHeaderData: () => null,
  getContentProps: () => ({}),
  onDataLoaded: null,
  footerMessage: DEFAULT_FOOTER_MESSAGE,
  footerLinks: null,
};

export default BrochurePage;
